import { Translation } from '../../helper';
import { Payment } from '../../helper/payment';
import { ReservationGuest } from '../../helper/reservations';
import { CartItem } from '../../routes/v03/order';

export enum EventTypes {
  DINNER_TICKET = 'dinner-ticket',
  EXPERIENCE = 'experience',
  MINIMUM_CONSUMPTION = 'minimum-consumption',
  PRE_ORDER = 'pre-order',
}

export declare enum PaymentStatus {
  open = 'open',
  canceled = 'canceled',
  pending = 'pending',
  authorized = 'authorized',
  expired = 'expired',
  failed = 'failed',
  paid = 'paid',
}

export enum PaidVia {
  STRIPE = 'stripe',
  MOLLIE = 'mollie',
  CASH = 'cash',
  VOUCHER = 'voucher',
  EXTERNAL = 'external',
}

export type DinnerEventOption = {
  id: string;
  pos?: number;
  translation: Translation<{ title: string; description: string }>;
  internalName: string;
  amount?: number;
  tables?: string[];
  img?: string;
  type: 'perGroup' | 'perPerson';
  limited?: number;
  vatRate: '19.00' | '7.00' | '0.00'; // should be 7% by default
  minAmount?: number | 'GROUP_SIZE' | null;
  wholeGroupOnly?: boolean;
};

export interface Event {
  id: string;
  active: boolean;
  edited?: boolean;
  title: Translation<string>;
  description: Translation<string>;
  learnMore?: string;
  type: EventTypes;
  amount: number;
  img?: string | null;
  per?: 'perGroup' | 'perPerson'; // defaults to perPerson
  optionPer?: 'perGroup' | 'perPerson';
  blockTableFor?: {
    value: number;
    unit: 'minutes' | 'hours' | 'days';
  }; // shows how long the table is blocked until it was paid
  minInAdvance?: {
    value: number;
    unit: 'minutes' | 'hours' | 'days';
  }; // shows how long this ticket can be booked in advance
  confirmation_email?: {
    active: boolean;
    translations: Translation<{
      title: string;
      description: string;
      button?: string;
    }>;
    image?: string | null;
    button?: string;
  } | null;
  reminder_email?: {
    active: boolean;
    translations: Translation<{
      title: string;
      description: string;
      button?: string;
    }>;
    image?: string | null;
    button?: string;
    sendBefore: {
      value: number;
      unit: 'minutes' | 'hours' | 'days';
    }; // how long before the event should this emal be send
  } | null;
  preOrder?: {
    menu?: null | string;
    enforce: boolean;
    oneDishEach?: boolean;
  }; // will be added later
  optional?: boolean; // optional ticket?
  rules?: {
    minGuests?: number;
    maxGuests?: number;
    daysTill?: number;
    excludeRegular?: boolean;
    excludeVip?: boolean;
    excludeOccassions?: string[];
  };
  ignoreTables?: boolean;
  payAtRestaurant?: boolean; // only when event is none optional
  partialPayment?: number;
  maxGuests?: number; // maximum number of guests that can buy this ticket
  options?: DinnerEventOption[];
  optionalAddOns?: DinnerEventOption[];
  disallowMultipleOptions?: boolean;

  offerQrCode?: boolean;

  /**
   * Free-ticket display flag. When `true`, guest-facing surfaces omit this
   * event's prices entirely (the price tag and the price breakdown); absent or
   * `false` shows them normally. Only valid for events that never charge —
   * treat as `false` for anything that can take a payment.
   */
  hidePrice?: boolean;

  siaMessage?: {
    prompt: Record<string, string>;
    action: {
      type: 'message' | 'redirect' | 'voiceMail';
      prompt?: Record<string, string>;
    };
  };
}

export interface Ticket {
  id?: string;
  eventId: string;
  customId: string;
  event: Event;
  date: string;
  restaurant: string;
  reservation: string;
  amount: number;
  guests: number;
  paid: boolean;
  guest: Partial<ReservationGuest>;
  payAtRestaurant?: boolean;
  partialPayment?: number;
  time: string;
  payments: {
    id?: string;
    invitationId?: string;
    amount: number;
    guest?: Partial<ReservationGuest>;
    paidVia: PaidVia;
    paymentId?: string;
    voucherId?: string;
    voucherProvider?: 'gurado';
    guradoReference?: string;
    createdAt?: number;
    cart?: CartItem[];
    status: PaymentStatus;
    options?: Record<string, number>;
    optionalAddOns?: Record<string, number>;
    paymentLink?: string;
    testmode: boolean;
    service_fee?: number;
    reference?: string | null;
    note?: string | null;
  }[];
  invitations?: {
    id: string;
    name: string;
    email: string;
  }[];
  status: 'missing_payments' | 'processing' | 'failed' | 'succedded';
  options?: Record<string, number>;
  optionalAddOns?: Record<string, number>;
  createdAt?: number;
  paymentValidTill?: number | null;
  /**
   * Set once the restaurant cancels the ticket from the dashboard. A cancelled
   * ticket cannot be reopened: webhooks only reconcile refunds and late payments.
   */
  cancellation?: TicketCancellation;
}

export type TicketRefundCalculationType =
  | 'full'
  | 'percentage'
  | 'fixed'
  | 'paidMinusFixed';

export type TicketRefundMethod = 'mollie' | 'voucher' | 'originalVoucher' | 'none';

export interface TicketRefundCalculation {
  type: TicketRefundCalculationType;
  /** Percent (0-100) for `percentage`, euros for `fixed` and `paidMinusFixed`. */
  value?: number | null;
  includeServiceFee?: boolean;
}

export interface TicketMollieRefund {
  paymentId: string;
  refundId: string;
  amount: number;
  status: string;
}

export interface TicketCancellation {
  status: 'processing' | 'completed' | 'attention';
  requestedAt: number;
  requestedBy: string;
  completedAt?: number;
  signature?: string | null;
  reason?: string;
  templateId?: string | null;
  templateName?: string | null;
  daysBeforeEvent?: number | null;
  refund?: {
    method: TicketRefundMethod;
    status?: 'pending' | 'refunded' | 'failed' | 'partial' | 'unknown';
    requestedAmount?: number;
    documentNumber?: string | null;
    amount: number;
    calculation: TicketRefundCalculation;
    mollieRefunds?: TicketMollieRefund[];
    voucherIssued?: boolean;
    /** Set when Mollie refunded less than requested. */
    error?: string | null;
  };
}

export interface TicketCounterDocument {
  date: string;
  restaurantId: string;
  eventId: string;
  options: Record<string, number>;
  optionalAddOns: Record<string, number>;
  count: number;
}

export interface EventV02Document extends Event {
  connectedShifts: {
    shiftId: string;
    date?: string;
  }[];
}

/**
 * Phase A — canonical global event. Lives at
 * `restaurants/{restaurantId}/eventsV03/{eventId}` and is populated by the
 * Phase B backfill from the legacy-id-cleaned tickets/shifts left behind by
 * Phase 0.
 *
 * Diff vs Event:
 *   - Drops `edited` (the shift-save marker has no meaning on the global doc).
 *   - Adds audit + lifecycle fields, `schemaVersion` discriminator, and
 *     `legacyEventIds[]` so `resolveEventByIdOrLegacyId()` can bridge tickets
 *     or URLs that still reference pre-rewrite ids.
 *
 * Phase A+B never flips the `useGlobalEvents` feature flag, so reads still
 * come from `shift.additional.eventV02`. This doc is shadow data until
 * Phase C.
 */
export interface EventV03Document extends Omit<Event, 'edited'> {
  schemaVersion: 3;
  createdAt: number;
  createdBy: string;
  updatedAt?: number;
  updatedBy?: string;
  archivedAt?: number | null;
  legacyEventIds?: string[];
}

/**
 * Phase A — alias bridge for event ids that changed during Phase 0
 * remediation. Lives at `restaurants/{restaurantId}/eventAliases/{aliasId}`
 * where `aliasId` is the legacy id. `getEventIdAliases()` checks this
 * collection after the canonical `eventsV03` lookup misses, so tickets or
 * URLs referencing a renormalized legacy id still resolve.
 *
 * Covers two cases:
 *   1. Renormalized polluted id pointing at a canonical linked event — the
 *      legacy id is also captured in `eventsV03.legacyEventIds[]`, but the
 *      alias doc gives us an O(1) direct lookup path.
 *   2. Renormalized polluted id pointing at a local-only event — there is no
 *      `eventsV03` doc for local-only events, so this is the only home for
 *      that mapping.
 */
export interface EventAliasDocument {
  aliasId: string;
  scope: 'linked' | 'local';
  target:
    | { scope: 'linked'; eventId: string }
    | { scope: 'local'; shiftId: string; date: string; localEventId: string };
  createdAt: number;
  createdBy: string;
}
